import type {
  EventTypeView,
  PopularAccumulatorsConfig,
  UpcomingEventsTab
} from '@ps/isp-web-services-types';
import { type RootState } from '@ps/web-core-store';
import { notEmpty } from '@ps/web-core-utils';
import { uniqBy } from 'lodash';
import type { Selector } from 'react-redux';
import { createSelector } from 'reselect';

import { withMeetingAndEventBasedMarketTemplateModelSelectors } from '@swf/sports/models/market-templates/meetingAndEventBased';

import { defaultState } from './defaultState';
import type { InPlayTabWithSlug, SportsHomePageState } from './types';

export const selectors = withMeetingAndEventBasedMarketTemplateModelSelectors(
  defaultState,
  (slice) => ({
    hasPreloaded: () => slice((state) => state.hasPreloaded),
    isLoading: () => slice((state) => state.isLoading),
    hasErrored: () => slice((state) => state.error),
    components: () => slice((state) => state.components),
    hasComponents: () => slice((state) => Boolean(state.components.length)),
    popularBetBuilders: () => slice((state) => state.popularBetBuilders),
    hasPopularBetBuilders: () => slice((state) => Boolean(state.popularBetBuilders.length)),
    popularSports: () => slice((state) => state.popularSports),
    hasPopularSports: () => slice((state) => Boolean(state.popularSports.length)),
    oddsBoostEvents: () => slice((state) => state.oddsBoosts),
    hasOddsBoostEvents: () => slice((state) => Boolean(state.oddsBoosts.length)),
    popularAccumulators: () => slice((state) => state.popularAccumulators),
    filteredPopularAccumulators(this: {
      popularAccumulators: Selector<RootState, SportsHomePageState['popularAccumulators']>;
      selectedPopularAccumulatorsEventType: Selector<
        RootState,
        SportsHomePageState['selectedPopularAccumulatorsEventType']
      >;
      markets: Selector<RootState, SportsHomePageState['markets']>;
    }) {
      return createSelector(
        this.popularAccumulators,
        this.selectedPopularAccumulatorsEventType,
        this.markets,
        (paccas, selectedEventType, markets): PopularAccumulatorsConfig[] =>
          paccas.filter(
            (pacca) => markets[pacca.runnerIds[0].marketId].eventTypeId === selectedEventType
          )
      );
    },
    hasPopularAccumulators: () => slice((state) => Boolean(state.popularAccumulators.length)),
    popularAccumulatorEventTypes(this: {
      popularAccumulators: Selector<RootState, SportsHomePageState['popularAccumulators']>;
      eventTypes: Selector<RootState, SportsHomePageState['eventTypes']>;
      markets: Selector<RootState, SportsHomePageState['markets']>;
    }) {
      return createSelector(
        this.popularAccumulators,
        this.eventTypes,
        this.markets,
        (paccas, eventTypes, markets): EventTypeView[] =>
          uniqBy(
            paccas
              .map((pacca) => {
                const market = markets[pacca.runnerIds[0].marketId];
                return eventTypes[market.eventTypeId];
              })
              .filter(notEmpty),
            (eventType) => eventType.eventTypeId
          )
      );
    },
    selectedPopularAccumulatorsEventType: () =>
      slice((state) => state.selectedPopularAccumulatorsEventType),
    selectedInPlayEventType: () => slice((state) => state.selectedInPlayEventType),
    inPlayTabsWithEventTypeSlugs: () =>
      slice((state) =>
        state.inPlayEvents.map((tab) => {
          const eventTypeView = state.eventTypes[tab.eventTypeId];
          return {
            ...tab,
            eventTypeSlug: eventTypeView?.eventTypeSlug ?? ''
          };
        })
      ),
    selectedInPlayTab(this: {
      inPlayTabsWithEventTypeSlugs: Selector<RootState, InPlayTabWithSlug[]>;
      selectedInPlayEventType: Selector<RootState, SportsHomePageState['selectedInPlayEventType']>;
    }) {
      return createSelector(
        this.inPlayTabsWithEventTypeSlugs,
        this.selectedInPlayEventType,
        (tabs, selectedEventType): InPlayTabWithSlug | undefined =>
          tabs.find((tab) => tab.eventTypeId === selectedEventType) ?? tabs[0]
      );
    },
    inPlayEvents: () => slice((state) => state.inPlayEvents),
    hasInPlayEvents: () => slice((state) => Boolean(state.inPlayEvents.length)),
    upcomingEvents: () =>
      slice((state) => {
        if (state.upcomingEvents.length > 0) return state.upcomingEvents[0].groups;
      }),
    hasUpcomingEvents: () => slice((state) => Boolean(state.upcomingEvents.length)),
    firstUpcomingEventsGroup: () =>
      slice((state) => {
        if (state.upcomingEvents.length > 0) return state.upcomingEvents[0];
      }),
    upcomingEventType(this: {
      eventTypes: Selector<RootState, SportsHomePageState['eventTypes']>;
      firstUpcomingEventsGroup: Selector<RootState, UpcomingEventsTab | undefined>;
    }) {
      return createSelector(
        this.eventTypes,
        this.firstUpcomingEventsGroup,
        (eventTypes, firstUpcomingEventsGroup): EventTypeView | undefined => {
          if (!firstUpcomingEventsGroup) return;
          const upcomingEventTypeId = firstUpcomingEventsGroup.eventTypeId;
          return eventTypes[upcomingEventTypeId];
        }
      );
    }
  })
);
